import { useEffect, useState } from "react";
import CtaButtonGroup from "@/components/molecules/CtaButtonGroup";
import { HeroV2Props } from "@/shared/data/home.v2.data";
import CreatorArc from "./shared/CreatorArc";
import Text from "./shared/Text";

const FLIP_WORDS = ["Creator", "Education", "Coaching"];
const FLIP_INTERVAL_MS = 3000;
const FLIP_ANIM_MS = 650;
const FLIP_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

// Slot-machine flip: the outgoing word rolls up & blurs out of a clipped
// frame while the incoming word rolls in from below. `previous` only
// exists during the transition so the frame otherwise sizes cleanly to
// the active word's natural width (no gaps in "Your ___ Business").
const FlippingWord = ({
  words,
  intervalMs,
}: {
  words: string[];
  intervalMs: number;
}) => {
  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((c) => {
        setPrevious(c);
        return (c + 1) % words.length;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [words.length, intervalMs]);

  useEffect(() => {
    if (previous === null) return undefined;
    const t = setTimeout(() => setPrevious(null), FLIP_ANIM_MS);
    return () => clearTimeout(t);
  }, [previous]);

  const animating = previous !== null;

  return (
    <span
      aria-live="polite"
      className="relative inline-block overflow-hidden whitespace-nowrap align-bottom text-primary"
      style={{ lineHeight: 1.15, verticalAlign: "-0.18em" }}
    >
      {/* Width spacer: sized to the current word so the surrounding copy
          re-centers to a tight, natural-width heading. */}
      <span className="invisible inline-block">{words[current]}</span>

      {/* Incoming word — absolute so it doesn't add width; rolls up from
          below and fades in. Keyed to `current` so the animation restarts
          on every change. */}
      <span
        key={`in-${current}`}
        className="absolute inset-0 inline-block"
        style={{
          animation: `flipWordIn ${FLIP_ANIM_MS}ms ${FLIP_EASING} both`,
        }}
      >
        {words[current]}
      </span>

      {/* Outgoing word — only mounted during transition. Rolls up and
          fades out of the frame. */}
      {animating && previous !== null && (
        <span
          key={`out-${previous}`}
          className="absolute inset-0 inline-block"
          style={{
            animation: `flipWordOut ${FLIP_ANIM_MS}ms ${FLIP_EASING} both`,
          }}
        >
          {words[previous]}
        </span>
      )}
    </span>
  );
};

/**
 * HeroV2 — "Your Creator Business / Faster and Smarter with AI"
 *
 * Anatomy:
 *   • Soft pink/peach radial-gradient wash behind the content.
 *   • Two-line headline (muted grey top / near-black bottom).
 *   • Subtitle, two CTAs.
 *   • A 3D perspective arc carousel of creator headshots below the CTAs —
 *     see `CreatorArc` — that slowly rotates around the vertical axis.
 *   • Four-up stats strip in a rounded white card.
 */

const HeroV2 = ({
  titleMuted,
  titleBright,
  description,
  ctas,
  stats,
  creatorAvatars,
}: HeroV2Props) => {
  return (
    <section className="relative w-full overflow-hidden bg-transparent">
      {/* Gradient lives on the HomeV2 wrapper so it can span both the
          navbar and the hero top — see HomeV2.tsx. */}

      {/* Symmetric vertical rhythm above the heading and below the CTAs —
          both sides use the same pt/pb scale so the hero copy sits in a
          balanced vertical block. Scale is ~70% of the previous rhythm. */}
      <div className="container relative z-10 mx-auto px-4 pb-10 pt-6 sm:px-6 sm:pt-8 md:px-8 md:pb-14 md:pt-14 lg:px-12 lg:pb-20 lg:pt-20">
        <div className="mx-auto max-w-4xl text-center">
          <Text
            as="h1"
            variant="display-lg"
            className="text-[24px] sm:text-[24px] md:text-[40px] lg:text-[48px]"
          >
            <span className="text-neutralBlack">
              {titleMuted.includes("Creator") ? (
                <>
                  {titleMuted.split("Creator")[0]}
                  <FlippingWord
                    words={FLIP_WORDS}
                    intervalMs={FLIP_INTERVAL_MS}
                  />
                  {titleMuted.split("Creator")[1]}
                </>
              ) : (
                titleMuted
              )}
            </span>
            <br />
            <span className="text-neutralBlack">{titleBright}</span>
          </Text>

          <Text
            as="p"
            variant="body-lg"
            className="mx-auto mt-4 max-w-2xl text-[14px] text-darkSlate04 md:mt-5 md:text-[16px]"
            style={{ letterSpacing: "-0.08px" }}
          >
            {description}
          </Text>

          <div className="mt-7 flex items-center justify-center md:mt-8">
            {ctas && ctas.length ? (
              // Equal-width CTAs: wrapper sets a bounded width and the
              // `[&_button]:w-full`/`[&_a]:w-full` selectors force both
              // Button elements to fill their equal 1fr grid cells so the
              // two pills always share the same width.
              <div className="w-full max-w-[480px] [&>div]:!w-full [&_a]:w-full [&_button]:w-full">
                <CtaButtonGroup
                  ctas={ctas}
                  gap="gap-3"
                  fullWidthOnMobile={true}
                />
              </div>
            ) : null}
          </div>
        </div>

        {/* Horizontally-scrollable creator showcase.
            The top `mt-*` scale mirrors the hero's `pt-*` scale so the gap
            between the CTA row and the creator images equals the gap
            between the navbar and the heading — a symmetric vertical
            rhythm around the hero copy block. */}
        <div className="mt-8 md:mt-14 lg:mt-20">
          <CreatorArc avatars={creatorAvatars} />
        </div>

        {/* Stats strip */}
        {stats && stats.length ? (
          <div className="mx-auto mt-10 grid max-w-5xl grid-cols-2 gap-4 rounded-2xl bg-white px-4 py-5 sm:gap-4 sm:rounded-3xl sm:px-6 sm:py-6 md:mt-12 md:grid-cols-4 md:gap-4 md:px-10 md:py-8">
            {stats.map((s) => (
              <div key={s.label} className="min-w-0 text-center">
                <div className="font-inter font-medium tracking-[-0.3px] text-neutralBlack text-[16px] leading-tight sm:text-[18px] sm:tracking-[-0.5px] md:text-[28px] md:tracking-[-0.6px] md:leading-none">
                  {s.value}
                </div>
                <div className="font-inter font-normal text-darkSlate04 mt-1 text-[11px] leading-tight sm:mt-2 sm:text-[12px] md:text-[14px]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HeroV2;
